import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    company: {
      name: 'BRIDGE iT',
      nameArabic: 'بريدج آي تي',
      activity: 'Développement de logiciels - Solutions informatiques',
      services: '(Applications web et mobiles) Systèmes de gestion - Consulting IT',
      address: 'Cité Technologique, Zone d\'Innovation N° 15',
      city: 'Alger',
      postalCode: 'BP 2024-16000',
      country: 'Algérie',
      phone: '021.65.43.21',
      email: 'contact@bridge-it.com',
      website: 'www.bridge-it.com',
      articleNumber: '1601240105',
      nif: '000000041987654',
      nis: '000000041987654',
      rc: '16/00-0024680',
      bankAccount1: '00007000750800205 CPA',
      bankAccount2: '20302000TECH330085 BADR ALGER',
      bankAccount3: '20302000003300085 BNA BATNA',
      logo: '/image1.png'
    },
    taxes: {
      tva: 19,
      tap: 2,
      vf: 1,
      defaultTaxes: ['tva'],
      taxIncluded: false
    },
          invoicing: {
        currency: 'DZD',
        language: 'fr',
        dateFormat: 'DD/MM/YYYY',
        invoicePrefix: 'FAC',
        quotePrefix: 'DEV',
        nextInvoiceNumber: 1001,
        nextQuoteNumber: 2001,
        paymentTerms: 30,
        lateFeesRate: 1.5,
        // Compteurs pour tous les types de documents
        documentCounters: {
          invoice: 1,
          proforma: 1,
          quoteRequest: 1,
          quote: 1,
          purchaseOrder: 1,
          attachment: 1,
          deliveryNote: 1,
          shippingSlip: 1,
          receptionNote: 1,
          returnSlip: 1,
          stockSheet: 1,
          materialExit: 1,
          creditNote: 1,
          accountStatement: 1,
          advanceInvoice: 1,
          paymentReceipt: 1,
          paymentSchedule: 1,
          commercialContract: 1,
          serviceAgreement: 1,
          complianceCertificate: 1,
          formalNotice: 1,
          receptionReport: 1,
          interventionReport: 1,
          nonComplianceSheet: 1,
          taxCertificate: 1,
          workCertificate: 1,
          legalNotice: 1,
          payslip: 1,
          workContract: 1,
          workAttestation: 1,
          leaveRequest: 1,
          productionSheet: 1,
          qualityControl: 1,
          manufacturingOrder: 1,
          failureReport: 1,
          routeSheet: 1,
          deliveryReport: 1,
          transportOrder: 1,
          rentalContract: 1,
          propertyInspection: 1,
          rentReceipt: 1,
          accidentReport: 1,
          securityReport: 1,
          safetyCompliance: 1,
          maintenanceReport: 1,
          maintenancePlan: 1,
          clientIntervention: 1,
          productWarranty: 1,
          itAudit: 1,
          softwareInstall: 1,
          itMaintenance: 1,
          backup: 1,
          purchaseRequest: 1,
          tenderCall: 1,
          supplierComparison: 1,
          goodsReception: 1,
          trainingPlan: 1,
          trainingEvaluation: 1,
          trainingCertificate: 1,
          prospectSheet: 1,
          clientVisitReport: 1,
          priceCatalog: 1,
          equipmentSheet: 1,
          calibration: 1,
          materialLoan: 1,
          dashboard: 1,
          financialReport: 1,
          marketStudy: 1,
          benchmark: 1
        }
      },
      notifications: {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        invoiceReminders: true,
        paymentAlerts: true,
        lowStockAlerts: false
      },
      design: {
        headerStyle: 'modern',
        colorScheme: 'blue',
        fontFamily: 'inter',
        showLogo: true,
        logoPosition: 'left',
        headerLayout: 'horizontal',
        documentTemplate: 'professional',
        borderStyle: 'clean',
        accentColor: '#1e88e5',
        backgroundColor: '#ffffff',
        textColor: '#000000',
        showWatermark: false,
        watermarkText: '',
        footerStyle: 'minimal',
        showPageNumbers: true
      }
  });

  // Charger les paramètres depuis localStorage au démarrage
  useEffect(() => {
    const savedSettings = localStorage.getItem('bridgeit-settings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings(parsed);
        
        // Set i18n language based on settings
        if (parsed.invoicing && parsed.invoicing.language) {
          i18n.changeLanguage(parsed.invoicing.language);
          
          // Set document direction based on language
          if (parsed.invoicing.language === 'ar') {
            document.documentElement.dir = 'rtl';
            document.documentElement.lang = 'ar';
          } else {
            document.documentElement.dir = 'ltr';
            document.documentElement.lang = parsed.invoicing.language;
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error);
      }
    }
  }, []);

  // Sauvegarder automatiquement les paramètres dans localStorage
  useEffect(() => {
    localStorage.setItem('bridgeit-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSettings = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    
    // Handle language change
    if (section === 'invoicing' && field === 'language') {
      i18n.changeLanguage(value);
      
      // Set document direction based on language
      if (value === 'ar') {
        document.documentElement.dir = 'rtl';
        document.documentElement.lang = 'ar';
      } else {
        document.documentElement.dir = 'ltr';
        document.documentElement.lang = value;
      }
    }
  };

  const incrementDocumentCounter = (docType) => {
    const counterMap = {
      'invoice': 'invoice',
      'proforma': 'proforma', 
      'quote-request': 'quoteRequest',
      'quote': 'quote',
      'purchase-order': 'purchaseOrder',
      'attachment': 'attachment',
      'delivery-note': 'deliveryNote',
      'shipping-slip': 'shippingSlip',
      'reception-note': 'receptionNote',
      'return-slip': 'returnSlip',
      'stock-sheet': 'stockSheet',
      'material-exit': 'materialExit',
      'credit-note': 'creditNote',
      'account-statement': 'accountStatement',
      'advance-invoice': 'advanceInvoice',
      'payment-receipt': 'paymentReceipt',
      'payment-schedule': 'paymentSchedule',
      'commercial-contract': 'commercialContract',
      'service-agreement': 'serviceAgreement',
      'compliance-certificate': 'complianceCertificate',
      'formal-notice': 'formalNotice',
      'reception-report': 'receptionReport',
      'intervention-report': 'interventionReport',
      'non-compliance-sheet': 'nonComplianceSheet',
      'tax-certificate': 'taxCertificate',
      'work-certificate': 'workCertificate',
      'legal-notice': 'legalNotice',
      'payslip': 'payslip',
      'work-contract': 'workContract',
      'work-attestation': 'workAttestation',
      'leave-request': 'leaveRequest',
      'production-sheet': 'productionSheet',
      'quality-control': 'qualityControl',
      'manufacturing-order': 'manufacturingOrder',
      'failure-report': 'failureReport',
      'route-sheet': 'routeSheet',
      'delivery-report': 'deliveryReport',
      'transport-order': 'transportOrder',
      'rental-contract': 'rentalContract',
      'property-inspection': 'propertyInspection',
      'rent-receipt': 'rentReceipt',
      'accident-report': 'accidentReport',
      'security-report': 'securityReport',
      'safety-compliance': 'safetyCompliance',
      'maintenance-report': 'maintenanceReport',
      'maintenance-plan': 'maintenancePlan',
      'client-intervention': 'clientIntervention',
      'product-warranty': 'productWarranty',
      'it-audit': 'itAudit',
      'software-install': 'softwareInstall',
      'it-maintenance': 'itMaintenance',
      'backup': 'backup',
      'purchase-request': 'purchaseRequest',
      'tender-call': 'tenderCall',
      'supplier-comparison': 'supplierComparison',
      'goods-reception': 'goodsReception',
      'training-plan': 'trainingPlan',
      'training-evaluation': 'trainingEvaluation',
      'training-certificate': 'trainingCertificate',
      'prospect-sheet': 'prospectSheet',
      'client-visit-report': 'clientVisitReport',
      'price-catalog': 'priceCatalog',
      'equipment-sheet': 'equipmentSheet',
      'calibration': 'calibration',
      'material-loan': 'materialLoan',
      'dashboard': 'dashboard',
      'financial-report': 'financialReport',
      'market-study': 'marketStudy',
      'benchmark': 'benchmark'
    };

    const counterKey = counterMap[docType];
    if (counterKey) {
      setSettings(prev => {
        const currentCounter = prev.invoicing.documentCounters?.[counterKey] || 1;
        return {
          ...prev,
          invoicing: {
            ...prev.invoicing,
            documentCounters: {
              ...prev.invoicing.documentCounters,
              [counterKey]: currentCounter + 1
            }
          }
        };
      });
    }
  };

  const resetSettings = () => {
    setSettings({
      company: {
        name: 'BRIDGE iT',
        nameArabic: 'بريدج آي تي',
        activity: 'Développement de logiciels - Solutions informatiques',
        services: '(Applications web et mobiles) Systèmes de gestion - Consulting IT',
        address: 'Cité Technologique, Zone d\'Innovation N° 15',
        city: 'Alger',
        postalCode: 'BP 2024-16000',
        country: 'Algérie',
        phone: '021.65.43.21',
        email: 'contact@bridge-it.com',
        website: 'www.bridge-it.com',
        articleNumber: '1601240105',
        nif: '000000041987654',
        nis: '000000041987654',
        rc: '16/00-0024680',
        bankAccount1: '00007000750800205 CPA',
        bankAccount2: '20302000TECH330085 BADR ALGER',
        logo: '/image1.png'
      },
      taxes: {
        tva: 19,
        tap: 2,
        vf: 1,
        defaultTaxes: ['tva'],
        taxIncluded: false
      },
      invoicing: {
        currency: 'DZD',
        language: 'fr',
        dateFormat: 'DD/MM/YYYY',
        invoicePrefix: 'FAC',
        quotePrefix: 'DEV',
        nextInvoiceNumber: 1001,
        nextQuoteNumber: 2001,
        paymentTerms: 30,
        lateFeesRate: 1.5,
        // Compteurs pour tous les types de documents
        documentCounters: {
          invoice: 1,
          proforma: 1,
          quoteRequest: 1,
          quote: 1,
          purchaseOrder: 1,
          attachment: 1,
          deliveryNote: 1,
          shippingSlip: 1,
          receptionNote: 1,
          returnSlip: 1,
          stockSheet: 1,
          materialExit: 1,
          creditNote: 1,
          accountStatement: 1,
          advanceInvoice: 1,
          paymentReceipt: 1,
          paymentSchedule: 1,
          commercialContract: 1,
          serviceAgreement: 1,
          complianceCertificate: 1,
          formalNotice: 1,
          receptionReport: 1,
          interventionReport: 1,
          nonComplianceSheet: 1,
          taxCertificate: 1,
          workCertificate: 1,
          legalNotice: 1,
          payslip: 1,
          workContract: 1,
          workAttestation: 1,
          leaveRequest: 1,
          productionSheet: 1,
          qualityControl: 1,
          manufacturingOrder: 1,
          failureReport: 1,
          routeSheet: 1,
          deliveryReport: 1,
          transportOrder: 1,
          rentalContract: 1,
          propertyInspection: 1,
          rentReceipt: 1,
          accidentReport: 1,
          securityReport: 1,
          safetyCompliance: 1,
          maintenanceReport: 1,
          maintenancePlan: 1,
          clientIntervention: 1,
          productWarranty: 1,
          itAudit: 1,
          softwareInstall: 1,
          itMaintenance: 1,
          backup: 1,
          purchaseRequest: 1,
          tenderCall: 1,
          supplierComparison: 1,
          goodsReception: 1,
          trainingPlan: 1,
          trainingEvaluation: 1,
          trainingCertificate: 1,
          prospectSheet: 1,
          clientVisitReport: 1,
          priceCatalog: 1,
          equipmentSheet: 1,
          calibration: 1,
          materialLoan: 1,
          dashboard: 1,
          financialReport: 1,
          marketStudy: 1,
          benchmark: 1
        }
      },
      notifications: {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        invoiceReminders: true,
        paymentAlerts: true,
        lowStockAlerts: false
      }
    });
  };

  const value = {
    settings,
    updateSettings,
    resetSettings,
    incrementDocumentCounter
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}; 